import { useCallback, useMemo } from "react";

import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";

import { MedalFilled } from "@/assets/icons/24/MedalFilled";
import { Security } from "@/assets/icons/24/Security";
import Star from "@/assets/icons/24/Star";
import { Tag } from "@/assets/icons/24/Tag";
import { Trophy } from "@/assets/icons/24/Trophy";
import ProfileIcon from "@/assets/icons/24/filled/Profile";
import {
  ProductBySlugNonNullable,
  ProductMerchant,
} from "@/common/contracts/pdp.contracts";
import { analyticsService } from "@/common/services/analytics/analytics.service";
import { USER_ADS_ROUTE } from "@/common/static/routes.static";
import { cn } from "@/common/utils/css.utils";
import { formatDateWithLocale } from "@/common/utils/intl.utils";
import { ProductDetailsSellerLabel } from "@/components/ProductDetail/ProductDetailsSellerLabel";
import { ProductDetailsInfoSellerContact } from "@/components/ProductDetail/info/seller/ProductDetailsInfoSellerContact";
import { ProductDetailsInfoSellerDate } from "@/components/ProductDetail/info/seller/ProductDetailsInfoSellerDate";
import { ProductDetailsInfoSellerLocation } from "@/components/ProductDetail/info/seller/ProductDetailsInfoSellerLocation";
import { ProductDetailsInfoSellerReviews } from "@/components/ProductDetail/info/seller/ProductDetailsInfoSellerReviews";
import useMerchant from "@/hooks/useMerchant";
import { useContentLocale } from "@/i18n/hooks";
import { Heading } from "@/ui/cva/Heading";
import { Body } from "@/ui/typography/Body";
import { MerchantType } from "@whoppah/apollo/schema";
import { showModal } from "@whoppah/ui/Modal2";

type ProductDetailsSellerBlockCardProps = {
  icon: JSX.Element;
  title: string;
  text: string;
  action?: JSX.Element;
  className?: string;
  onActionClick?: () => void;
};

const ProductDetailsSellerBlockCard = ({
  icon,
  title,
  text,
  action,
  onActionClick,
  className,
}: ProductDetailsSellerBlockCardProps) => {
  return (
    <button
      type="button"
      className={cn(
        "flex items-center rounded border border-grey-300 p-4 text-left",
        action ? "cursor-pointer" : "cursor-default",
        className
      )}
      onClick={() => onActionClick?.()}
    >
      <div className="flex items-center space-x-6">
        <div className="flex min-h-12 min-w-12 items-center justify-center rounded-full border border-brand-500 md:hidden lg:flex">
          {icon}
        </div>
        <div className="flex flex-col items-start">
          <Heading as="h5">{title}</Heading>
          <Body className="text-left text-base">{text}</Body>
          {action}
        </div>
      </div>
    </button>
  );
};

const ProductDetailsSellerTypeCard = ({
  merchant,
}: {
  merchant: ProductMerchant;
}) => {
  const { t } = useTranslation();

  const businessMerchant = merchant.type === MerchantType.Business;

  const handlePrivateSellerClick = useCallback(() => {
    showModal("pdp-details-seller-block-modal", {
      title: t("pdp:pdp-info-private-seller"),
      descLokaliseKey: "pdp:pdp-info-private-seller-desc-modal",
    });
  }, [t]);

  const handleBusinessSellerClick = useCallback(() => {
    showModal("pdp-details-seller-block-modal", {
      title: t("pdp:pdp-info-professional-seller-label"),
      descLokaliseKey: "pdp:pdp-info-professional-seller-desc-modal",
    });
  }, [t]);

  const seller = useMemo(() => {
    if (businessMerchant) {
      return {
        icon: <MedalFilled className="text-brand-500" />,
        title: t("pdp:pdp-info-professional-seller-label"),
        text: t("pdp:pdp-info-professional-seller-text"),
        action: (
          <span className="self-start text-brand-500 underline">
            {t("pdp:pdp-info-seller-block-read-more-business-btn")}
          </span>
        ),
        onActionClick: handleBusinessSellerClick,
      };
    }

    return {
      icon: <ProfileIcon className="text-brand-500" />,
      title: t("pdp:pdp-info-private-seller"),
      text: t("pdp:pdp-info-private-seller-text"),
      action: (
        <span className="self-start text-brand-500 underline">
          {t("common:read-more")}
        </span>
      ),
      onActionClick: handlePrivateSellerClick,
    };
  }, [
    businessMerchant,
    t,
    handleBusinessSellerClick,
    handlePrivateSellerClick,
  ]);

  return (
    <ProductDetailsSellerBlockCard
      icon={seller.icon}
      title={seller.title}
      text={seller.text}
      action={seller.action}
      onActionClick={seller.onActionClick}
      className="hover:bg-gray-50"
    />
  );
};

const ProductDetailsSellerActiveAdsCard = ({
  merchant,
}: {
  merchant: ProductMerchant;
}) => {
  const { t } = useTranslation();
  const router = useRouter();

  const numberOfAds = merchant.number_of_ads ?? 0;
  const merchantId = merchant.id;

  if (!numberOfAds) {
    return null;
  }

  /*
   * Navigates on click rather than rendering an anchor: `/user-ads` is robots-disallowed and a
   * crawlable link to it from every PDP still got 1.47M such URLs discovered (Search Console,
   * 2026-09-15).
   */
  const handleClick = () => router.push(`${USER_ADS_ROUTE}/${merchantId}`);

  return (
    <ProductDetailsSellerBlockCard
      icon={<Tag className="text-brand-500" />}
      title={t("pdp:pdp-seller-active-ads-title")}
      text={t("pdp:pdp-seller-active-ads-text", { ads: numberOfAds })}
      action={
        <span className="font-body text-brand-500 underline">
          {t("pdp:pdp-seller-info-view-all-seller-ads")}
        </span>
      }
      className="hover:bg-gray-50"
      onActionClick={handleClick}
    />
  );
};

const ProductDetailsSellerSalesNumberCard = ({
  merchant,
}: {
  merchant: ProductMerchant;
}) => {
  const { t } = useTranslation();
  const locale = useContentLocale();

  const merchantCreatedAt = formatDateWithLocale(
    merchant.created,
    "MMMM YYYY",
    locale
  );

  const numberOfSells = merchant.number_of_sells ?? 0;

  if (!numberOfSells) {
    return null;
  }

  return (
    <ProductDetailsSellerBlockCard
      icon={<Security className="text-brand-500" />}
      title={t("pdp:pdp-seller-sales-title")}
      text={t("pdp:pdp-seller-sales-text", {
        ads: numberOfSells,
        date: merchantCreatedAt,
      })}
    />
  );
};

const ProductDetailsSellerReviewsCard = ({
  merchant,
}: {
  merchant: ProductMerchant;
}) => {
  const { t } = useTranslation();

  const merchantRating = merchant.rating ?? 0;

  const handleClick = useCallback(() => {
    showModal("merchant-reviews", {
      merchant,
    });

    /** Trigger `readmore_reviews_pdp` event. */
    analyticsService.trackReadMoreReviewsPdp();
  }, [merchant]);

  const seller = useMemo(() => {
    // no reviews yet
    if (!merchantRating) {
      return {
        icon: <Star className="text-brand-500" />,
        title: t("pdp:pdp-seller-reviews-title"),
        text: t("pdp:pdp-seller-no-reviews-yet"),
      };
    }

    // top rated seller
    if (merchantRating >= 4.5) {
      return {
        icon: <Trophy className="text-brand-500" />,
        title: t("pdp:pdp-seller-reviews-top-rated-title"),
        text: t("pdp:pdp-seller-reviews-top-rated-text", {
          rating: merchantRating,
        }),
        action: (
          <span className="self-start text-brand-500 underline">
            {t("pdp:pdp-seller-reviews-read-reviews-cta")}
          </span>
        ),
        onActionClick: handleClick,
      };
    }

    // regular seller
    return {
      icon: <Star className="text-brand-500" />,
      title: t("pdp:pdp-seller-reviews-title"),
      text: t("pdp:pdp-seller-reviews-regular-text"),
      action: (
        <span className="self-start text-brand-500 underline">
          {t("pdp:pdp-seller-reviews-read-reviews-cta")}
        </span>
      ),
      onActionClick: handleClick,
    };
  }, [handleClick, merchantRating, t]);

  return (
    <ProductDetailsSellerBlockCard
      icon={seller.icon}
      title={seller.title}
      text={seller.text}
      action={seller.action}
      onActionClick={seller.onActionClick}
      className={merchantRating ? "hover:bg-gray-50" : ""}
    />
  );
};

type ProductDetailsSellerBlockProps = {
  product: ProductBySlugNonNullable;
};

export const ProductDetailsSellerBlock = ({
  product,
}: ProductDetailsSellerBlockProps) => {
  const { t } = useTranslation();
  const { id: userId } = useMerchant();

  const merchant = product.merchant;
  const isProductOwner = product.merchant.id === userId;

  return (
    <div className="md:col-span-3">
      <Heading as="h4" className="mb-6">
        {t("pdp:pdp-seller-info-block-title")}
      </Heading>
      <div className="grid grid-rows-2 gap-x-10 md:grid-cols-3 md:grid-rows-none">
        <div className="row-span-2 mb-4 flex flex-col space-y-4 text-sm md:col-span-1">
          <div className="flex w-full flex-wrap items-center">
            <Heading as="h5" className="mr-2">
              {t("pdp:pdp-seller-block-title")}
            </Heading>
            <ProductDetailsSellerLabel
              className="self-start rounded"
              merchantType={merchant.type}
              sellerIsOwner={isProductOwner}
            />
          </div>
          <div className="flex flex-col space-y-2">
            <ProductDetailsInfoSellerReviews product={product} />
            <ProductDetailsInfoSellerLocation product={product} />
            <ProductDetailsInfoSellerDate product={product} />
          </div>
          <ProductDetailsInfoSellerContact product={product} />
        </div>
        <div className="grid h-fit auto-rows-fr gap-4 md:col-span-2 md:grid-cols-2">
          <ProductDetailsSellerTypeCard merchant={merchant} />
          <ProductDetailsSellerReviewsCard merchant={merchant} />
          <ProductDetailsSellerActiveAdsCard merchant={merchant} />
          <ProductDetailsSellerSalesNumberCard merchant={merchant} />
        </div>
      </div>
    </div>
  );
};
